import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import React, { ChangeEvent, useEffect, useState } from "react";
import { FormControl, FormGroup, InputGroup, Nav, NavDropdown } from "react-bootstrap";
import { useNavigate } from 'react-router-dom';
import { ApplicationFunctions, useAuth } from "../../contexts/authContext";
import { useNotifications } from "../../contexts/notificationContext";
import { useTranslations } from "../../contexts/translationContext";
import NotificationModel from '../../models/NotificationModel';
import WorkflowTaskQueryModel from "../../models/WorkflowTaskQueryModel";
import DataService from "../../services/data.service";
import { Result } from "../../types/Result";
import TranslationsSearchPopup from "../../views/AppConfig/Popups/TranslationsSearchPopup";
import { ItemSearchCriteria } from '../../views/Search/FilterForm/ItemFilterFormAdvanced';
import LanguageMenu from "./LanguageMenu";
import NotificationDropdownItem from './NotificationDropdownItem';
import WorkflowTaskNavDropdownItem from "./WorkflowTaskNavDropdownItem";

const HeaderLinks: React.FC = () => {
    const [quickSearch, setQuickSearch] = useState<string>('');
    const [unverifiedCount, setUnverifiedCount] = useState<number>(0);
    const [unapprovedCount, setUnapprovedCount] = useState<number>(0);
    const [messageCount, setMessageCount] = useState<number>(0);
    const [notificationCount, setNotificationCount] = useState<number>(0);
    const [overdueTaskCount, setOverdueTaskCount] = useState<number>(0);
    const [openTaskCount, setOpenTaskCount] = useState<number>(0);
    const [openTasks, setOpenTasks] = useState<WorkflowTaskQueryModel[]>([])
    const [unreadNotifications, setUnreadNotifications] = useState<NotificationModel[]>([])
    const [showTranslationsPopup, setShowTranslationsPopup] = useState(false)
    const [connection, setConnection] = useState<HubConnection>();
    const { user, logout } = useAuth();
    const { handleException, handleResult, notify } = useNotifications();
    const navigate = useNavigate();
    const { isAuthorizedFor, isAuthorizedForWorkflowDefinition } = useAuth();
    const { clearTranslationScope } = useTranslations()


    useEffect(() => {
        getAlertsRecursive();
        getMessages();
    }, [])

    function getAlertsRecursive()
    {
        getAlerts();

        // Every 15 minutes
        setTimeout(() => getAlertsRecursive(), 900000);
    }

    function getAlerts() {
        if (isAuthorizedForWorkflowDefinition(ApplicationFunctions.ExecuteAllWorkflowTasks) ||
            isAuthorizedForWorkflowDefinition(ApplicationFunctions.ExecuteMyWorkflowTasks)) {

            DataService.get<number>('api/v1/WorkflowTasks/overduetaskcount')
                .then(response => {
                    setOverdueTaskCount(response);
                })
                .catch(e => { handleException(e); });

            DataService.get<number>('api/v1/WorkflowTasks/opentaskcount')
                .then(response => {
                    setOpenTaskCount(response);
                })
                .catch(e => { handleException(e); });
            /*
            DataService.get<WorkflowTaskQueryModel[]>('api/v1/WorkflowTasks/myopentasks')
                .then(response => {
                    setOpenTasks(response);
                })
                .catch(e => { handleException(e); });
            */
        }
    }

    function getMessages() {
        getMessagesData()
        setTimeout(() => getMessages(), 300000); //Every 5 minutes
    };

    function getMessagesData() {
        if (isAuthorizedFor(ApplicationFunctions.ViewMessage)) {
            DataService.get<number>('api/v1/Messages/unreadcount')
                .then(response => {
                    setMessageCount(response);
                })
                .catch(e => { handleException(e); });
        }

        if (isAuthorizedFor(ApplicationFunctions.ViewNotification)) {

            DataService.get<number>('api/v1/Notifications/unreadcount')
                .then(response => {
                    setNotificationCount(response);
                })
                .catch(e => { handleException(e); });

            DataService.get<NotificationModel[]>('api/v1/Notifications/myunreadnotifications')
                .then(response => {
                    setUnreadNotifications(response);
                })
                .catch(e => { handleException(e); });
        }

        if (isAuthorizedFor(ApplicationFunctions.ViewTranslations)) {

            DataService.get<number>('api/v1/Translations/unapprovedcount')
                .then(response => {
                    setUnapprovedCount(response);
                })
                .catch(e => { handleException(e); });

            DataService.get<number>('api/v1/Translations/unverifiedcount')
                .then(response => {
                    setUnverifiedCount(response);
                })
                .catch(e => { handleException(e); });
        }
    };


    useEffect(() => {
        if (user) {
            let url: string | undefined
            switch (user.environment)
            {
                case 'dev':
                    url = process.env.REACT_APP_API_ENDPOINT_dev;
                    break;
                case 'tst':
                    url = process.env.REACT_APP_API_ENDPOINT_tst;
                    break;
                case 'prd':
                    url = process.env.REACT_APP_API_ENDPOINT_prd;
                    break;
            }

            const newConnection = new HubConnectionBuilder()
                .withUrl(url + 'hubs/notificationhub', { accessTokenFactory: () => user.token })
                .withAutomaticReconnect()
                .build();

            setConnection(newConnection);
        }
    }, [user?.token]);

    useEffect(() => {
        if (connection) {
            connection.start()
                .then(result => {
                    //notify("success", "Connected to notification hub.")
                    console.log('Connected to notification hub!');

                    connection.on('ReceiveNotification', notification => {
                        setNotificationCount(notificationCount + 1)
                    });

                    connection.on('ReceiveMessage', message => {
                        setMessageCount(messageCount + 1)
                    });

                    connection.on('ReceiveResult', (result: Result) => {
                        handleResult(result);
                    });

                    connection.on('UpdateTasks', () => {
                        getAlerts();
                    });
                })
                .catch(e => {
                    //notify("warning", "Unable to connect to notification hub.")
                    console.log('Connection to notification hub failed: ', e)
                });
        }
    }, [connection]);

    function logoutUser() {
        logout();
        navigate('/plm/home', { state: { title: 'Home', fromMenu: false } });
    }

    function onSearchChanged(e: ChangeEvent<HTMLInputElement>) {
        setQuickSearch(e.target.value);
    }
    function onSearchKeyUp(e) {
        if (e.keyCode === 13) { // Enter key
            search();
        }
    }

    function search() {
        let rememberFilter = false
        const filterItem = localStorage.getItem("itemFilterFormAdvanced");
        if (filterItem !== null) {
            let filterAdvanced: ItemSearchCriteria = JSON.parse(filterItem);
            rememberFilter = filterAdvanced.rememberFilter
        }

        const filter = {
            quickSearch: quickSearch,
            itemTypeIds: [],
            brandId: 0,
            productCategoryId: 0,
            employeeIds: [],
            projectIds: [],
            itemStatusIds: [],
            designPhases: [],
            includeUnsearchableItems: true,
            searchInBomItems: false,
            activeOnly: true,
            mainItemsOnly: true,
            fromNavBar: true,
            rememberFilter: rememberFilter,

            pageNumber: 1,
            pageSize: 100,
            sortBy: 'CreatedOn'
        }
        setQuickSearch('');
        localStorage.setItem('itemFilterFormAdvanced', JSON.stringify(filter));
        navigate('/plm/Search/ItemNavBar', { state: { title: 'Seach itemrevisions', fromMenu: false } });
    }


    let translationsCount = 0
    let translationsBadge = ''
    if (unapprovedCount > 0) {
        translationsCount = unapprovedCount
        translationsBadge = 'badge-default'
    }
    else if (unverifiedCount > 0) {
        translationsCount = unverifiedCount
        translationsBadge = 'badge-success'
    }

    let taskCount = 0
    let taskBadge = ''
    if (overdueTaskCount > 0) {
        taskCount = overdueTaskCount
        taskBadge = 'badge-danger'
    }
    else if (openTaskCount > 0) {
        taskCount = openTaskCount
        taskBadge = 'badge-success'
    }

    return (
        <>
            <div className="navbar-search-form navbar-form navbar-left">
                <FormGroup>
                    <InputGroup>
                        <InputGroup.Text>
                            <i className="fa fa-search" onClick={search} />
                        </InputGroup.Text>
                        <FormControl type="text" value={quickSearch} placeholder="Search..." onChange={onSearchChanged} onKeyUp={onSearchKeyUp} />
                    </InputGroup>
                </FormGroup>
            </div>
            <Nav className="nav navbar-right">
                {translationsCount > 0 && (
                    <Nav.Item onClick={() => setShowTranslationsPopup(true) /*navigate('/plm/AppConfig/Translations')*/} >
                        <div style={{ margin: 15 }}>
                            <i className="fa fa-globe" />
                                <span className={'badge ' + translationsBadge}>{translationsCount}</span>
                        </div>
                    </Nav.Item>
                )}
                {unreadNotifications.length > 0 && (
                    <NavDropdown id="basic-nav-dropdown-2" title={
                        <div>
                            <i className="fa fa-bell-o" />
                            {notificationCount > 0 && <span className="badge badge-danger">{notificationCount}</span>}
                        </div>
                    }>
                        {
                            unreadNotifications.map((item, key) => {
                                return (<NotificationDropdownItem notification={item} onNotificationChanged={getMessagesData} key={key + item.concurrencyId} />)
                            })
                        }
                    </NavDropdown>
                )}
                {messageCount > 0 && (
                    <NavDropdown id="basic-nav-dropdown-2" title={
                        <div>
                            <i className="pe-7s-mail" />
                            <span className={'badge badge-danger'}>{messageCount}</span>
                        </div>
                    }>
                        <NavDropdown.Item>Message 1</NavDropdown.Item>
                        <NavDropdown.Item>Message 2</NavDropdown.Item>
                        <NavDropdown.Item>Message 3</NavDropdown.Item>
                        <NavDropdown.Item>Message 4</NavDropdown.Item>
                        <NavDropdown.Item>Another message</NavDropdown.Item>
                    </NavDropdown>
                )}
                {taskCount > 0 && (
                    <Nav.Item onClick={() => navigate('/plm/workflow/mytasks')} >
                        <div style={{ margin: 15 }}>
                            <i className="fa fa-th-list" />
                            {overdueTaskCount > 0 && <span className={'badge badge-overduetaskcount'} >{overdueTaskCount}</span>}
                            {openTaskCount > 0 && <span className={'badge badge-opentaskcount'} style={{ marginLeft: -5 }}>{openTaskCount}</span>}
                        </div>
                    </Nav.Item>
                    /* 
                <NavDropdown id="basic-nav-dropdown-2" className="dropdown-with-icons" title={
                    <div>
                        <i className="fa fa-th-list" />
                        <span className={'badge ' + taskBadge}>{taskCount}</span>
                    </div>
                    }>
                    <NavDropdown.Item onClick={() => navigate('/plm/workflow/mytasks')}>
                        <div><i className="fa fa-gears" />My Tasks</div>
                    </NavDropdown.Item>
                    <NavDropdown.Divider />
                    {
                        openTasks.map((item, key) => {
                            return (<WorkflowTaskNavDropdownItem task={item} key={key} />)
                        })
                    }
            </NavDropdown>
                    */
                )}
                {user && (<>
                    <LanguageMenu />
                    <NavDropdown id="basic-nav-dropdown-4" className="dropdown-with-icons" title={
                        <div>
                            <div className="thumbnail" style={{ marginTop: -5 }}>
                                <img height={32} src={user.thumbnailUrl} />
                            </div>
                            <p>{user.name}</p>
                        </div>
                    }>
                        <NavDropdown.Item onClick={() => navigate('/plm/workflow/mytasks')}>
                            <div><i className="fa fa-gears" />My Tasks</div>
                        </NavDropdown.Item>
                        <NavDropdown.Item onClick={clearTranslationScope}>
                            <div><i className="fa fa-refresh" />Refresh translations</div>
                        </NavDropdown.Item>

                        <NavDropdown.Item onClick={logoutUser}>
                            <div className="text-danger"><i className="pe-7s-close-circle" />Log out</div>
                        </NavDropdown.Item>
                    </NavDropdown>
                </>)}
            </Nav>
            {showTranslationsPopup && <TranslationsSearchPopup show={showTranslationsPopup} onHide={() => { setShowTranslationsPopup(false) }} />}
        </>
    );
}
export default HeaderLinks;
